import { Captions, ChevronLeft, ChevronRight } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";
import { Media } from "@/services/bedrock";

import { formatQaTimestamp } from "./qaIssues";
import {
  activeComparisonRowIndex,
  activeTimedLineIndex,
  activeTimedPartIndex,
  clampScrollTop,
  comparisonDifferenceCount,
  comparisonRowAsTimedLine,
  DiffPart,
  QaStudioSource,
  scrollTopToCenterElement,
  shouldPollTranscriptStatus,
  shouldShowOnScreenStack,
  shouldShowSpokenStack,
  shouldShowTranscriptComparison,
  spokenLinesFromMedia,
  srtCuesFromMedia,
  TimedLine,
  timedPartAsTimedLine,
  TRANSCRIPT_SHORTCUT,
  TranscriptComparisonRow,
  transcriptComparisonFromMedia,
  wordingDifferenceCount,
} from "./qaStudio";

export function TranscriptLyrics({
  media,
  source,
  currentSeconds,
  highlighted,
  collapsed,
  onToggleCollapsed,
  onSeekLine,
}: {
  media?: Media;
  source?: QaStudioSource;
  currentSeconds: number;
  highlighted?: boolean;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  onSeekLine: (line: TimedLine) => void;
}) {
  const showOnScreen = shouldShowOnScreenStack(media, source);
  const showSpoken = shouldShowSpokenStack(media);
  const showTranscriptLabel = `Show transcript (${TRANSCRIPT_SHORTCUT})`;
  const hideTranscriptLabel = `Hide transcript (${TRANSCRIPT_SHORTCUT})`;
  const toggleTranscriptLabel = collapsed ? showTranscriptLabel : hideTranscriptLabel;
  const onScreenLines = srtCuesFromMedia(media);
  const spoken = spokenLinesFromMedia(media);
  const generating = shouldPollTranscriptStatus(media);
  const failed = media?.transcriptStatus === "FAILED";
  const comparisonRows = shouldShowTranscriptComparison(media, source) ? transcriptComparisonFromMedia(media) : [];
  const comparing = comparisonRows.length > 0;
  const wordingDiff = comparing
    ? comparisonDifferenceCount(comparisonRows)
    : onScreenLines.length && spoken.lines.length
    ? wordingDifferenceCount(
        onScreenLines.map(line => line.text).join(" "),
        spoken.lines.map(line => line.text).join(" ")
      )
    : null;
  const onScreenActiveIndex = activeTimedLineIndex(onScreenLines, currentSeconds * 1000);
  const spokenActiveIndex = activeTimedLineIndex(spoken.lines, currentSeconds * 1000);
  const comparisonActiveIndex = activeComparisonRowIndex(comparisonRows, currentSeconds * 1000);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const onScreenActiveRef = React.useRef<HTMLButtonElement>(null);
  const spokenActiveRef = React.useRef<HTMLButtonElement>(null);
  const comparisonActiveRef = React.useRef<HTMLButtonElement>(null);
  const followPlayheadRef = React.useRef(true);
  const followStackRef = React.useRef<"on-screen" | "spoken" | "comparison">(
    comparing ? "comparison" : showOnScreen ? "on-screen" : "spoken"
  );
  const programmaticUntilRef = React.useRef(0);
  const [fadeTop, setFadeTop] = React.useState(false);
  const [fadeBottom, setFadeBottom] = React.useState(false);

  const centerActiveLine = React.useCallback((element?: HTMLElement | null) => {
    const container = scrollRef.current;
    const target =
      element ||
      (followStackRef.current === "comparison"
        ? comparisonActiveRef.current
        : followStackRef.current === "spoken"
        ? spokenActiveRef.current
        : onScreenActiveRef.current) ||
      comparisonActiveRef.current ||
      onScreenActiveRef.current ||
      spokenActiveRef.current;
    if (!container || !target || !followPlayheadRef.current) return;
    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const nextTop = clampScrollTop(
      scrollTopToCenterElement(
        container.scrollTop,
        containerRect.top,
        container.clientHeight,
        targetRect.top,
        targetRect.height
      ),
      container.scrollHeight,
      container.clientHeight
    );
    programmaticUntilRef.current = Date.now() + 500;
    container.scrollTo({ top: nextTop, behavior: "smooth" });
  }, []);

  const seekFromStack = React.useCallback(
    (stack: "on-screen" | "spoken" | "comparison", line: TimedLine) => {
      followPlayheadRef.current = true;
      followStackRef.current = stack;
      onSeekLine(line);
      window.requestAnimationFrame(() => centerActiveLine());
    },
    [centerActiveLine, onSeekLine]
  );

  React.useEffect(() => {
    followPlayheadRef.current = true;
    followStackRef.current = comparing ? "comparison" : showOnScreen ? "on-screen" : "spoken";
  }, [comparing, media?.id, showOnScreen]);

  const updateFadeEdges = React.useCallback(() => {
    const container = scrollRef.current;
    if (!container) {
      setFadeTop(false);
      setFadeBottom(false);
      return;
    }
    setFadeTop(container.scrollTop > 4);
    setFadeBottom(container.scrollTop + container.clientHeight < container.scrollHeight - 4);
  }, []);

  React.useEffect(() => {
    if (collapsed) {
      setFadeTop(false);
      setFadeBottom(false);
      return;
    }
    const container = scrollRef.current;
    if (!container) return;
    const pauseFollowFromScroll = () => {
      if (Date.now() < programmaticUntilRef.current) return;
      followPlayheadRef.current = false;
    };
    const pauseFollowFromPointer = () => {
      followPlayheadRef.current = false;
      programmaticUntilRef.current = 0;
    };
    const onScroll = () => {
      pauseFollowFromScroll();
      updateFadeEdges();
    };
    container.addEventListener("wheel", pauseFollowFromPointer, { passive: true });
    container.addEventListener("touchmove", pauseFollowFromPointer, { passive: true });
    container.addEventListener("scroll", onScroll, { passive: true });
    updateFadeEdges();
    const observer = new ResizeObserver(updateFadeEdges);
    observer.observe(container);
    return () => {
      container.removeEventListener("wheel", pauseFollowFromPointer);
      container.removeEventListener("touchmove", pauseFollowFromPointer);
      container.removeEventListener("scroll", onScroll);
      observer.disconnect();
    };
  }, [collapsed, updateFadeEdges]);

  React.useEffect(() => {
    if (!followPlayheadRef.current) return;
    centerActiveLine();
  }, [centerActiveLine, comparisonActiveIndex, onScreenActiveIndex, spokenActiveIndex]);

  const panelClass = highlighted ? "border-amber-400 bg-amber-950/40" : "border-slate-800 bg-slate-900";
  const transcriptMask =
    fadeTop && fadeBottom
      ? "linear-gradient(to bottom, transparent, black 1.5rem, black calc(100% - 2rem), transparent)"
      : fadeTop
      ? "linear-gradient(to bottom, transparent, black 1.5rem, black)"
      : fadeBottom
      ? "linear-gradient(to bottom, black, black calc(100% - 2rem), transparent)"
      : undefined;

  return (
    <aside
      className={cn(
        "relative shrink-0 overflow-visible border-l",
        collapsed ? "w-9" : comparing ? "w-[min(38rem,52%)]" : "w-[min(22rem,40%)]",
        panelClass
      )}
    >
      <button
        type="button"
        title={toggleTranscriptLabel}
        aria-label={toggleTranscriptLabel}
        aria-expanded={!collapsed}
        className="absolute top-1/2 left-0 z-20 flex size-6 -translate-x-1/2 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-slate-600 bg-slate-900 text-slate-300 shadow-md hover:bg-slate-800 hover:text-white"
        onClick={onToggleCollapsed}
      >
        {collapsed ? <ChevronLeft className="size-3.5" /> : <ChevronRight className="size-3.5" />}
      </button>
      {collapsed ? (
        <button
          type="button"
          title={showTranscriptLabel}
          aria-label={showTranscriptLabel}
          className="flex h-full min-h-0 w-full cursor-pointer flex-col items-center gap-2 px-1.5 py-4 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
          onClick={onToggleCollapsed}
        >
          <Captions className="size-3.5 shrink-0" />
          <span className="text-[10px] font-semibold uppercase tracking-wide [writing-mode:vertical-rl]">
            Transcripts
          </span>
        </button>
      ) : (
        <div className="flex h-full min-h-0 flex-col overflow-hidden">
          <div className="flex items-center justify-between gap-3 px-5 pt-4 pb-2">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              <Captions className="size-3.5" />
              Transcript
            </div>
            {comparing && wordingDiff != null ? (
              <p className="text-[11px] text-slate-400">
                {wordingDiff} wording difference{wordingDiff === 1 ? "" : "s"}
              </p>
            ) : null}
          </div>
          {comparing ? (
            <div className="grid grid-cols-2 gap-3 px-5 pb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              <span>On-screen</span>
              <span>Spoken</span>
            </div>
          ) : null}
          <div
            ref={scrollRef}
            className="min-h-0 flex-1 overflow-y-auto px-5 pt-2 pb-8"
            style={transcriptMask ? { maskImage: transcriptMask, WebkitMaskImage: transcriptMask } : undefined}
          >
            {comparing ? (
              <TranscriptComparisonRows
                rows={comparisonRows}
                activeIndex={comparisonActiveIndex}
                activeRef={comparisonActiveRef}
                currentSeconds={currentSeconds}
                onSeekLine={line => seekFromStack("comparison", line)}
              />
            ) : (
              <div className="flex flex-col gap-6">
                {showOnScreen ? (
                  <section className="flex flex-col gap-3">
                    <h4 className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">On-screen</h4>
                    {onScreenLines.length ? (
                      <TimedLyricLines
                        lines={onScreenLines}
                        activeIndex={onScreenActiveIndex}
                        activeRef={onScreenActiveRef}
                        preserveLineBreaks
                        onSeekLine={line => seekFromStack("on-screen", line)}
                      />
                    ) : (
                      <p className="text-sm text-slate-500">No on-screen captions</p>
                    )}
                  </section>
                ) : null}
                {showSpoken ? (
                  <section className="flex flex-col gap-3">
                    <h4 className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Spoken</h4>
                    {failed ? (
                      <p className="text-sm text-slate-500">Spoken transcript failed</p>
                    ) : generating && !spoken.lines.length && !spoken.untimed.length ? (
                      <p className="text-sm text-slate-500">Generating spoken transcript…</p>
                    ) : spoken.lines.length ? (
                      <TimedLyricLines
                        lines={spoken.lines}
                        activeIndex={spokenActiveIndex}
                        activeRef={spokenActiveRef}
                        currentSeconds={currentSeconds}
                        onSeekLine={line => seekFromStack("spoken", line)}
                      />
                    ) : spoken.untimed.length ? (
                      <div className="flex flex-col gap-4">
                        {spoken.untimed.map((line, index) => (
                          <p
                            key={`${index}-${line.slice(0, 24)}`}
                            className="text-lg leading-8 font-medium text-slate-200"
                          >
                            {line}
                          </p>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500">No spoken transcript</p>
                    )}
                  </section>
                ) : null}
                {wordingDiff != null ? (
                  <p className="text-[11px] text-slate-500">
                    {wordingDiff} wording difference{wordingDiff === 1 ? "" : "s"}
                  </p>
                ) : null}
              </div>
            )}
          </div>
        </div>
      )}
    </aside>
  );
}

function TranscriptComparisonRows({
  rows,
  activeIndex,
  activeRef,
  currentSeconds,
  onSeekLine,
}: {
  rows: TranscriptComparisonRow[];
  activeIndex: number;
  activeRef: React.Ref<HTMLButtonElement>;
  currentSeconds: number;
  onSeekLine: (line: TimedLine) => void;
}) {
  const currentMs = currentSeconds * 1000;
  return (
    <div className="flex flex-col gap-3">
      {rows.map((row, index) => {
        const active = index === activeIndex;
        const timed = comparisonRowAsTimedLine(row);
        const className = cn(
          "grid w-full grid-cols-2 gap-3 py-1.5 text-left text-lg leading-7 font-medium transition-colors",
          timed && "cursor-pointer",
          active ? "text-white" : "text-slate-500",
          timed && !active && "hover:text-indigo-300",
          active && "border-l-2 border-amber-400 pl-2"
        );
        const body = (
          <>
            <DiffCell parts={row.onScreenParts} empty={!row.onScreenText} />
            <DiffCell
              parts={row.spokenParts}
              empty={!row.spokenText}
              currentMs={currentMs}
              onSeekPart={part => {
                const line = timedPartAsTimedLine(part);
                if (line) onSeekLine(line);
              }}
            />
          </>
        );
        if (!timed) {
          return (
            <div key={`${index}-${(row.spokenText || "").slice(0, 24)}`} className={className}>
              {body}
            </div>
          );
        }
        return (
          <button
            key={`${index}-${row.startMs}-${(row.onScreenText || row.spokenText || "").slice(0, 24)}`}
            ref={active ? activeRef : undefined}
            type="button"
            title={formatQaTimestamp(row.startMs) || undefined}
            className={className}
            onClick={() => onSeekLine(timed)}
          >
            {body}
          </button>
        );
      })}
    </div>
  );
}

function spokenWordClass(active?: boolean) {
  return active
    ? "rounded-sm bg-amber-300 text-slate-950 underline decoration-amber-400 underline-offset-4"
    : undefined;
}

function DiffCell({
  parts,
  empty,
  currentMs,
  onSeekPart,
}: {
  parts: DiffPart[];
  empty?: boolean;
  currentMs?: number;
  onSeekPart?: (part: DiffPart) => void;
}) {
  if (empty || !parts.length) {
    return <span className="text-slate-600">—</span>;
  }
  const activePartIndex = currentMs == null ? -1 : activeTimedPartIndex(parts, currentMs);
  return (
    <span className="min-w-0 whitespace-pre-wrap">
      {parts.map((part, index) => {
        const timed = part.startMs != null;
        return (
          <React.Fragment key={`${index}-${part.text}`}>
            {index > 0 ? " " : null}
            <span
              className={cn(
                timed && "cursor-pointer",
                part.kind === "remove" && index !== activePartIndex && "rounded-sm bg-red-500/25 text-red-300",
                part.kind === "add" && index !== activePartIndex && "rounded-sm bg-emerald-500/25 text-emerald-300",
                spokenWordClass(index === activePartIndex)
              )}
              onClick={
                timed && onSeekPart
                  ? event => {
                      event.stopPropagation();
                      onSeekPart(part);
                    }
                  : undefined
              }
            >
              {part.text}
            </span>
          </React.Fragment>
        );
      })}
    </span>
  );
}

function TimedLyricLines({
  lines,
  activeIndex,
  activeRef,
  preserveLineBreaks,
  currentSeconds,
  onSeekLine,
}: {
  lines: TimedLine[];
  activeIndex: number;
  activeRef: React.Ref<HTMLButtonElement>;
  preserveLineBreaks?: boolean;
  currentSeconds?: number;
  onSeekLine: (line: TimedLine) => void;
}) {
  const currentMs = (currentSeconds ?? 0) * 1000;
  return (
    <div className="flex flex-col gap-4">
      {lines.map((line, index) => {
        const active = index === activeIndex;
        const spokenWords = line.words?.filter(word => word.word);
        const activeWordIndex = spokenWords?.length ? activeTimedPartIndex(spokenWords, currentMs) : -1;
        return (
          <button
            key={`${index}-${line.startMs}-${line.text.slice(0, 24)}`}
            ref={active ? activeRef : undefined}
            type="button"
            title={formatQaTimestamp(line.startMs) || undefined}
            className={cn(
              "cursor-pointer text-left text-lg leading-8 font-medium transition-colors",
              preserveLineBreaks && "whitespace-pre-wrap",
              active ? "text-white" : "text-slate-500 hover:text-indigo-300",
              active && "border-l-2 border-amber-400 pl-2"
            )}
            onClick={() => onSeekLine(line)}
          >
            {spokenWords?.length
              ? spokenWords.map((word, wordIndex) => (
                  <React.Fragment key={`${wordIndex}-${word.startMs}-${word.word}`}>
                    {wordIndex > 0 ? " " : null}
                    <span
                      className={cn("cursor-pointer", spokenWordClass(wordIndex === activeWordIndex))}
                      onClick={event => {
                        event.stopPropagation();
                        onSeekLine({ startMs: word.startMs, endMs: word.endMs, text: word.word });
                      }}
                    >
                      {word.word}
                    </span>
                  </React.Fragment>
                ))
              : line.text}
          </button>
        );
      })}
    </div>
  );
}
